// ABOUTME: Sends a file's bytes into a Cloud Storage resumable upload session (gql-api docs/frontend-video.md §3.2): in
// ABOUTME: chunks, asking the session how far it got after any break and going on from what it holds.
import { abortError, isCancelled, pause } from "./abortable"

/** 8 MiB: a multiple of 256 KiB, as every chunk but the last must be. */
export const CHUNK_BYTES = 8 * 1024 * 1024
/** A request that moves no byte for this long has broken off — a chunk, a question, or the wait after the last byte. */
export const STALL_MS = 60_000
/** Breaks in a row after which the transfer stops, to be tried again by hand (↻). */
export const MAX_FAILURES = 6
const MAX_PAUSE_MS = 30_000

/** What a PUT to the session answered: its status, and the `Range` of a 308 — what the session holds so far. */
export interface SessionAnswer {
  status: number
  range: string | null
}

export interface SessionRequest {
  session: string
  /** A chunk, or null to ask how far the upload got. */
  body: Blob | null
  contentRange: string
  signal: AbortSignal
  /** The bytes of this request's body sent so far. */
  onSent: (bytes: number) => void
}

/** One PUT to the session: answers what came back, rejects when the connection broke or the request was aborted. */
export type SessionTransport = (request: SessionRequest) => Promise<SessionAnswer>

/** The session was cancelled or outlived its week (404, 410): the upload starts over under a new clientUploadId. */
export class SessionGoneError extends Error {
  constructor() {
    super("The upload session is gone")
    this.name = "SessionGoneError"
  }
}

/**
 * The transfer stopped. Retryable — breaks in a row, or a session answer that could not be read: another try (↻) asks
 * the session again. Otherwise the session refused the bytes, and would again. The session and its answers stay out
 * of the message: the session is a key to the upload.
 */
export class SessionFailedError extends Error {
  constructor(
    readonly isRetryable: boolean,
    reason: string,
  ) {
    super(reason)
    this.name = "SessionFailedError"
  }
}

/**
 * The browser's PUT. XMLHttpRequest, not fetch: only it reports the progress of a body on its way, and a chunk of
 * 8 MiB on a mobile network is a minute with no movement otherwise. No credentials and no headers of the app go to
 * storage: the session is its own key. A 308 without Location comes back as it is — only a redirect with one is
 * followed.
 */
export const xhrTransport: SessionTransport = ({ session, body, contentRange, signal, onSent }) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", session)
    xhr.setRequestHeader("Content-Range", contentRange)
    xhr.upload.onprogress = (event) => onSent(event.loaded)
    xhr.onload = () => resolve({ status: xhr.status, range: xhr.getResponseHeader("Range") })
    xhr.onerror = () => reject(new Error("The upload broke off"))
    xhr.onabort = () => reject(abortError())
    signal.addEventListener("abort", () => xhr.abort(), { once: true })
    xhr.send(body)
  })

/** Runs one request under a watchdog: no byte moved for STALL_MS — the request is aborted as a break. */
async function watched<T>(outer: AbortSignal, run: (signal: AbortSignal, moved: () => void) => Promise<T>): Promise<T> {
  const controller = new AbortController()
  const stop = () => controller.abort()
  outer.addEventListener("abort", stop, { once: true })
  let timer = setTimeout(stop, STALL_MS)
  const moved = () => {
    clearTimeout(timer)
    timer = setTimeout(stop, STALL_MS)
  }
  try {
    return await run(controller.signal, moved)
  } finally {
    clearTimeout(timer)
    outer.removeEventListener("abort", stop)
  }
}

/**
 * How many bytes the session holds, by the `Range` of a 308: `bytes=0-N` holds N + 1; none holds nothing. Anything
 * else is a protocol error, not a guess at the position.
 */
function heldBy(range: string | null, size: number): number {
  if (range === null) return 0
  const match = /^bytes=0-(\d+)$/.exec(range.trim())
  const last = match?.[1] === undefined ? Number.NaN : Number(match[1])
  // All of it held, yet not finished: a session answer the protocol has no place for.
  if (!Number.isSafeInteger(last) || last + 1 >= size) {
    throw new SessionFailedError(true, "The session answered a Range that cannot be read")
  }
  return last + 1
}

export interface SendOptions {
  session: string
  file: Blob
  signal: AbortSignal
  /** Ask the session first: the upload may hold bytes already — a retry, or a start repeated with the same id. */
  resume: boolean
  /** The bytes the session holds plus those of the chunk on its way, never more than the file. */
  onProgress: (bytes: number) => void
  transport?: SessionTransport
}

/** What one request came to: all of it in, what the session holds now, or a break to pause after and ask. */
type Outcome = { kind: "done" } | { kind: "held"; bytes: number } | { kind: "break"; reason: string }

/** What the session's answer means for the transfer; a refusal and a gone session end it. */
function outcomeOf({ status, range }: SessionAnswer, size: number): Outcome {
  if (status === 200 || status === 201) return { kind: "done" }
  if (status === 404 || status === 410) throw new SessionGoneError()
  if (status === 308) return { kind: "held", bytes: heldBy(range, size) }
  // A slow request timed out (408), too many (429), storage's own trouble (5xx): Google's client retries all three
  // (gql-api vendor/cloud.google.com/go/storage/invoke.go:205-207).
  if (status === 408 || status === 429 || status >= 500)
    return { kind: "break", reason: `The session answered ${status}` }
  throw new SessionFailedError(false, `The session refused the bytes with ${status}`)
}

/**
 * Sends the file into the session; resolves once the session answers that all of it is in (200, 201). A break — the
 * connection, a 408, a 429, a 5xx, a request without movement, or a chunk the session took none of — pauses 1, 2, 4… s,
 * asks the session how far it got and goes on from there. The breaks counter starts over only when the session holds
 * more; MAX_FAILURES in a row stop the transfer.
 */
export async function sendToSession(options: SendOptions): Promise<void> {
  const { session, file, signal, onProgress, transport = xhrTransport } = options
  const size = file.size
  let held = 0
  let failures = 0
  let isAsking = options.resume

  const breakOff = async (reason: string) => {
    failures += 1
    if (failures >= MAX_FAILURES) throw new SessionFailedError(true, reason)
    await pause(Math.min(MAX_PAUSE_MS, 1000 * 2 ** (failures - 1)), signal)
    isAsking = true
  }

  /** A chunk from where the session holds up to, or the question how far it got. */
  const request = async (): Promise<Outcome> => {
    const from = held
    const end = Math.min(from + CHUNK_BYTES, size)
    const asks = isAsking
    try {
      const answer = await watched(signal, (requestSignal, moved) =>
        transport({
          session,
          body: asks ? null : file.slice(from, end),
          contentRange: asks ? `bytes */${size}` : `bytes ${from}-${end - 1}/${size}`,
          signal: requestSignal,
          onSent: (bytes) => {
            moved()
            if (!asks) onProgress(Math.min(size, from + bytes))
          },
        }),
      )
      return outcomeOf(answer, size)
    } catch (error) {
      if (isCancelled(signal)) throw abortError()
      if (error instanceof SessionGoneError || error instanceof SessionFailedError) throw error
      return { kind: "break", reason: error instanceof Error ? error.message : "The upload broke off" }
    }
  }

  for (;;) {
    if (isCancelled(signal)) throw abortError()
    const outcome = await request()
    if (outcome.kind === "done") {
      onProgress(size)
      return
    }
    if (outcome.kind === "break") {
      await breakOff(outcome.reason)
      continue
    }
    const hasMoved = outcome.bytes > held
    held = outcome.bytes
    onProgress(held)
    if (hasMoved) failures = 0
    if (!isAsking && !hasMoved) await breakOff("The session took none of the chunk")
    else isAsking = false
  }
}
